<template>
  <Modal position="end" ref="modalRef">
    <template #header>Syndication platforms</template>
    <template #body>
      <View direction="column" gap="lg">
        <Text variant="body-medium" color="secondary">
          Choose where your listing will be published. Your unit will be listed on the selected platforms.
        </Text>

        <FormGroup>
          <FormField label="Available platforms" direction="column">
            <View direction="column" gap="md">
              <SyndicationPlatformCard
                v-for="platform in platformOptions"
                :key="platform.key"
                v-model="form.fields[platform.key]"
                :logo="platform.logo"
                :name="platform.name"
                :description="platform.description"
                :status="statusFor(platform.groupName)"
              />
            </View>
          </FormField>
        </FormGroup>
      </View>
    </template>
    <template #footer>
      <Button variant="ghost" @click="close">Cancel</Button>
      <Button @click="save" :loading="form.submitting || isUpdating">Save</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import apartmentsLogo from 'icons/apartments-logo.png';
import realtorLogo from 'icons/realtor-logo.png';
import zillowLogo from 'icons/zillow-logo.png';
import zumperLogo from 'icons/zumper-logo.png';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { Button, FormField, FormGroup, Modal, Text, useForm, View } from '@tallo/design-system';
import { usePropertySyndicationPlatformsQuery, useUpdateSyndicationPlatforms } from '@tallo/investor/property';

import SyndicationPlatformCard from './syndication-platform-card.vue';

interface SyndicationPlatformsForm {
  zillow: boolean;
  zumper: boolean;
  apartmentsDotCom: boolean;
  realtorDotCom: boolean;
}

// Display metadata for each platform toggle. `groupName` matches the backend
// syndication group name returned by the platforms query.
interface PlatformOption {
  key: keyof SyndicationPlatformsForm;
  groupName: string;
  name: string;
  description: string;
  logo: string;
}

const platformOptions: PlatformOption[] = [
  {
    key: 'zillow',
    groupName: 'zillow',
    name: 'Zillow',
    description: 'Also includes Hotpads and Trulia',
    logo: zillowLogo,
  },
  {
    key: 'zumper',
    groupName: 'zumper',
    name: 'Zumper',
    description: 'Rental marketplace platform',
    logo: zumperLogo,
  },
  {
    key: 'apartmentsDotCom',
    groupName: 'apartments-dot-com',
    name: 'Apartments.com',
    description: 'Apartment listing marketplace',
    logo: apartmentsLogo,
  },
  {
    key: 'realtorDotCom',
    groupName: 'realtor-dot-com',
    name: 'Realtor.com',
    description: 'Real estate listing site',
    logo: realtorLogo,
  },
];

const route = useRoute();
const propertyId = computed(() => route.params.propertyId as string);

const { enabledPlatforms, platforms } = usePropertySyndicationPlatformsQuery();
const { mutateAsync: updateSyndicationPlatforms, isLoading: isUpdating } = useUpdateSyndicationPlatforms();
const modalRef = ref<InstanceType<typeof Modal>>();

// Surface each platform's live syndication status next to its toggle, so editing happens
// in full view of what is currently posted where.
function statusFor(groupName: string): { label: string; color: 'green' | 'red' | 'grey' } | null {
  const platform = platforms.value.find((item) => item.name?.toLowerCase() === groupName.toLowerCase());
  if (!platform) {
    return null;
  }

  const comments = (platform.comments || '').toLowerCase().trim();
  if (comments === 'posted') {
    return { label: 'Posted', color: 'green' };
  }
  if (comments === 'starting syndication') {
    return { label: 'Publishing…', color: 'grey' };
  }
  if (comments.includes('not posted')) {
    return { label: 'Not posted', color: 'red' };
  }

  return null;
}

const form = useForm<SyndicationPlatformsForm>({
  zillow: false,
  zumper: false,
  apartmentsDotCom: false,
  realtorDotCom: false,
});

// Initialize form based on current syndication platforms
watch(
  () => enabledPlatforms.value,
  (currentPlatforms) => {
    platformOptions.forEach((platform) => {
      form.fields[platform.key] = currentPlatforms.includes(platform.groupName);
    });
  },
  { immediate: true },
);

async function save() {
  await form.submit(async (fields) => {
    try {
      await updateSyndicationPlatforms({
        propertyId: propertyId.value,
        syndicationDto: fields,
      });

      close();
    } catch (error) {
      console.error('Error saving syndication platforms:', error);
    }
  });
}

function close() {
  modalRef.value?.close();
}

defineExpose({
  modalRef,
});
</script>
